<template>
  <div>
    <div class="bg-blue-900 p-4 lg:p-12">
      <div class="w-full mx-auto mb-12">
        <div class="flex flex-col md:flex-row md:items-center">
          <h4 class="text-white font-bold">Tasks</h4>

          <div class="flex md:ml-auto mt-4 md:mt-0">
            <BaseButton info class="ml-auto md:ml-4" @click="filtersVisible = !filtersVisible">
              Filters
            </BaseButton>
          </div>
        </div>
      </div>
    </div>
    <div class="px-4 pb-4 lg:px-12 lg:pb-12 -mt-12">
      <div class="w-full mx-auto">
        <div class="space-y-16">
          <Filters
            v-if="filtersVisible"
            :filters="filters"
            @filters:applied="filtersVisible = false"
            @filters:cancel="filtersVisible = false"
          />
          <VList :meta="meta">
            <template #before>
              <VListHeader />
            </template>

            <template v-if="isLoading" #default>
              <div class="odd:bg-gray-100 border border-gray-200 rounded p-4 text-center">
                <Loader />
              </div>
            </template>

            <template v-else-if="isRejected" #default>
              <div
                class="odd:bg-red-100 border border-red-200 rounded p-4 text-red-600 text-center"
              >
                Something went wrong. Please try again later.
              </div>
            </template>

            <template v-else-if="tasks.length" #default>
              <template v-for="task in tasks">
                <VListLine
                  :key="`tasks_${task.uuid}`"
                  :task="task"
                  :disabled="isLoading"
                  @task:view="viewTask"
                />
              </template>
            </template>

            <template v-else #default>
              <div class="odd:bg-gray-100 border border-gray-200 rounded p-4 text-center">
                No results found
              </div>
            </template>
          </VList>
        </div>
      </div>
    </div>

    <portal v-if="selectedTaskId" to="app-modal">
      <TaskModal :task-id="selectedTaskId" @task-modal:closed="selectedTaskId = null" />
    </portal>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import store from '@/store';
import { tasksClient } from '@/api/clients/admin';
import VList from '@/components/Common/VList.vue';
import VListHeader from '@/components/Tasks/VListHeader.vue';
import VListLine from '@/components/Tasks/VListLine.vue';
import Filters from '@/components/Tasks/Filters.vue';
import TaskModal from '@/components/Tasks/TaskModal.vue';

const STATUS_IDLE = 'idle';
const STATUS_PENDING = 'pending';
const STATUS_RESOLVED = 'resolved';
const STATUS_REJECTED = 'rejected';

export default {
  name: 'Tasks',

  components: {
    VList,
    VListHeader,
    VListLine,
    Filters,
    TaskModal,
  },

  data: () => ({
    status: STATUS_IDLE,
    selectedTaskId: null,
    filters: {},
    filtersVisible: false,
  }),

  computed: {
    ...mapState('admin/tasks', ['meta']),
    ...mapGetters('admin/tasks', ['tasks']),
    isLoading() {
      return this.status === STATUS_PENDING;
    },
    isResolved() {
      return this.status === STATUS_RESOLVED;
    },
    isRejected() {
      return this.status === STATUS_REJECTED;
    },
  },

  watch: {
    $route: 'fetchData',
  },

  async beforeRouteEnter(to, from, next) {
    const {
      query: { sort = '', ...restQuery },
    } = to;

    const sortParams = sort.split(',');
    sortParams.push('-created_at');

    try {
      await store.dispatch('admin/tasks/load', {
        params: {
          ...restQuery,
          include: 'account,assignee',
          sort: sortParams.join(','),
        },
      });

      next();
    } catch (error) {
      next(false);
    }
  },

  created() {
    this.init();
  },

  destroyed() {
    this.setIds([]);
    this.setMeta(null);
    this.setLinks(null);
  },

  methods: {
    ...mapActions('admin/tasks', {
      loadTasks: 'load',
    }),

    ...mapMutations('admin/tasks', ['setIds', 'setMeta', 'setLinks']),

    async init() {
      try {
        await this.fetchFilters();
      } catch (error) {
        /** handle error */
      }
    },

    async fetchFilters() {
      try {
        const response = await tasksClient.filters();
        this.filters = response.data.data;
      } catch (error) {
        /** handle error */
      }
    },

    async fetchData() {
      const {
        query: { sort = '', ...restQuery },
      } = this.$route;

      const sortParams = sort.split(',');
      sortParams.push('-created_at');

      this.status = STATUS_PENDING;

      try {
        await this.loadTasks({
          params: {
            ...restQuery,
            include: 'account,assignee',
            sort: sortParams.join(','),
          },
        });

        this.status = STATUS_RESOLVED;
      } catch (error) {
        this.status = STATUS_REJECTED;
      }
    },

    viewTask(task) {
      this.selectedTaskId = task.uuid;
    },
  },
};
</script>
